import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormArray, FormBuilder, FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogActions, MatDialogContent, MatDialogRef, MatDialogTitle } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBar } from '@angular/material/snack-bar';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { ArrayResponse } from 'src/app/models/array_response.interface';
import { ExamTemplate, ExamTemplateBody } from 'src/app/models/exam_template.model';
import { Skill } from 'src/app/models/skill.model';
import { SkillQuestion } from 'src/app/models/skill_question.model';
import { ApiService } from 'src/app/services';

export interface ExamTemplateFormDialogData {
  mode: 'create' | 'edit';
  exam_template_id?: number;
}

@Component({
  selector: 'app-exam-template-form-dialog',
  templateUrl: './exam-template-form-dialog.component.html',
  styleUrl: './exam-template-form-dialog.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatSelectModule,
    MatAutocompleteModule,
  ],
})
export class ExamTemplateFormDialogComponent implements OnInit {
  private readonly dialog_ref = inject(MatDialogRef<ExamTemplateFormDialogComponent, boolean>);
  private readonly api_service = inject(ApiService);
  private readonly snack_bar = inject(MatSnackBar);
  private readonly destroy_ref = inject(DestroyRef);
  private readonly form_builder = inject(FormBuilder);

  readonly data = inject<ExamTemplateFormDialogData>(MAT_DIALOG_DATA);

  readonly loading = signal(false);
  readonly loading_data = signal(false);
  readonly skill_questions = signal<SkillQuestion[]>([]);
  readonly skills = signal<Skill[]>([]);
  readonly selected_skill_question = signal<SkillQuestion | null>(null);
  readonly selected_skill_id = signal<number | null>(null);
  readonly is_edit_mode = this.data.mode === 'edit';

  readonly question_search_control = new FormControl('', { nonNullable: true });

  readonly form = this.form_builder.group({
    name: ['', [Validators.required, Validators.maxLength(255)]],
    description: [''],
    items: this.form_builder.array([]),
  });

  readonly available_skill_questions = computed(() => {
    const used_skill_question_ids = new Set(
      this.items_form_array.controls.map(item_control => item_control.get('skill_question_id')?.value as number)
    );

    return this.skill_questions().filter(skill_question => !used_skill_question_ids.has(skill_question.id));
  });

  ngOnInit(): void {
    this.loadSkills();
    this.listenQuestionSearch();
    this.loadSkillQuestions();

    if (this.is_edit_mode && this.data.exam_template_id) {
      this.loadExamTemplate(this.data.exam_template_id);
    }
  }

  get items_form_array(): FormArray {
    return this.form.controls.items;
  }

  getSkillQuestionLabel(skill_question_id: number): string {
    const skill_question = this.skill_questions().find(sq => sq.id === skill_question_id);

    if (!skill_question) {
      return `#${skill_question_id}`;
    }

    const body_excerpt = skill_question.body.length > 80 ? `${skill_question.body.slice(0, 80)}…` : skill_question.body;

    return `#${skill_question.id} — ${body_excerpt}`;
  }

  displaySkillQuestion(skill_question: SkillQuestion | null): string {
    if (skill_question === null) {
      return '';
    }

    const body_excerpt = skill_question.body.length > 80 ? `${skill_question.body.slice(0, 80)}…` : skill_question.body;

    return `#${skill_question.id} — ${body_excerpt}`;
  }

  onSkillFilterChanged(skill_id: number | null): void {
    this.selected_skill_id.set(skill_id);
    this.loadSkillQuestions();
  }

  onSkillQuestionSelected(skill_question: SkillQuestion): void {
    this.selected_skill_question.set(skill_question);
  }

  addItemFromSelector(): void {
    const skill_question = this.selected_skill_question();

    if (skill_question === null) {
      return;
    }

    this.items_form_array.push(
      this.form_builder.group({
        skill_question_id: [skill_question.id, Validators.required],
        weight: [1, [Validators.required, Validators.min(1)]],
      })
    );

    this.selected_skill_question.set(null);
    this.question_search_control.setValue('');
  }

  removeItem(item_index: number): void {
    this.items_form_array.removeAt(item_index);
  }

  moveItemUp(item_index: number): void {
    if (item_index <= 0) {
      return;
    }

    const item_control = this.items_form_array.at(item_index);
    this.items_form_array.removeAt(item_index);
    this.items_form_array.insert(item_index - 1, item_control);
  }

  moveItemDown(item_index: number): void {
    if (item_index >= this.items_form_array.length - 1) {
      return;
    }

    const item_control = this.items_form_array.at(item_index);
    this.items_form_array.removeAt(item_index);
    this.items_form_array.insert(item_index + 1, item_control);
  }

  save(): void {
    if (this.loading() || this.loading_data() || this.form.invalid) {
      return;
    }

    if (this.items_form_array.length === 0) {
      this.snack_bar.open('Añade al menos una pregunta a la plantilla.', undefined, { duration: 4000 });
      return;
    }

    this.loading.set(true);

    const raw = this.form.getRawValue();
    const body: ExamTemplateBody = {
      name: raw.name ?? '',
      description: raw.description || null,
      items: this.items_form_array.controls.map((item_control, item_index) => {
        const item_value = item_control.getRawValue() as { skill_question_id: number; weight: number };

        return {
          skill_question_id: item_value.skill_question_id,
          weight: Math.max(1, Math.round(item_value.weight ?? 1)),
          sort_order: item_index,
        };
      }),
    };

    const request$ =
      this.is_edit_mode && this.data.exam_template_id
        ? this.api_service.updateExamTemplate(this.data.exam_template_id, body)
        : this.api_service.createExamTemplate(body);

    request$.pipe(takeUntilDestroyed(this.destroy_ref)).subscribe({
      next: (): void => {
        this.dialog_ref.close(true);
      },
      error: (err: { error?: { error?: string } }): void => {
        this.loading.set(false);
        const message = err?.error?.error ?? 'Error al guardar la plantilla.';
        this.snack_bar.open(message, undefined, { duration: 5000 });
      },
    });
  }

  cancel(): void {
    this.dialog_ref.close(false);
  }

  private listenQuestionSearch(): void {
    this.question_search_control.valueChanges
      .pipe(debounceTime(400), distinctUntilChanged(), takeUntilDestroyed(this.destroy_ref))
      .subscribe((): void => {
        this.loadSkillQuestions();
      });
  }

  private loadSkills(): void {
    this.api_service
      .getSkills({ per_page: 500, order_by: 'name:asc' })
      .pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe({
        next: (response: ArrayResponse<Skill>): void => {
          this.skills.set(response.data);
        },
      });
  }

  private loadSkillQuestions(): void {
    const search_term = this.question_search_control.value.trim();
    const params: Record<string, unknown> = {
      per_page: 100,
      order_by: 'id:desc',
      with: 'skills',
    };

    if (search_term !== '') {
      params['scopes'] = `search:${search_term}`;
    }

    const skill_id = this.selected_skill_id();
    if (skill_id !== null) {
      params['skill_id'] = skill_id;
    }

    this.api_service
      .getSkillQuestions(params)
      .pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe({
        next: (response: ArrayResponse<SkillQuestion> | SkillQuestion[]): void => {
          this.skill_questions.set(Array.isArray(response) ? response : response.data);
        },
      });
  }

  private loadExamTemplate(exam_template_id: number): void {
    this.loading_data.set(true);

    this.api_service
      .getExamTemplate(exam_template_id, { with: 'exam_template_items' })
      .pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe({
        next: (exam_template: ExamTemplate): void => {
          const sorted_items = [...(exam_template.exam_template_items ?? [])].sort(
            (first_item, second_item) => first_item.sort_order - second_item.sort_order
          );

          this.form.patchValue({
            name: exam_template.name,
            description: exam_template.description ?? '',
          });

          this.items_form_array.clear();

          sorted_items.forEach(exam_template_item => {
            this.items_form_array.push(
              this.form_builder.group({
                skill_question_id: [exam_template_item.skill_question_id, Validators.required],
                weight: [exam_template_item.weight, [Validators.required, Validators.min(1)]],
              })
            );
          });

          this.loading_data.set(false);
        },
        error: (): void => {
          this.loading_data.set(false);
          this.snack_bar.open('No se pudo cargar la plantilla.', undefined, { duration: 5000 });
          this.dialog_ref.close(false);
        },
      });
  }
}
